/**
 * Lectura de Excels de facturas RECIBIDAS + aplicación de un perfil de mapeo
 * para producir filas normalizadas listas para `buildReceivedSuenlaceFile`.
 *
 * Comparte especificación con `issuedInvoiceImport.ts`; se duplican los
 * helpers privados para desacoplar completamente ambos flujos y evitar
 * regresiones al tocar uno.
 */

import ExcelJS from "exceljs";
import { parseNumericLike } from "@/lib/numberFormatting";
import {
  columnLetterToIndex,
  previewExcel as previewExcelIssued,
} from "@/lib/issuedInvoiceImport";
import type {
  ReceivedColumnMap,
  ReceivedDefaults,
  ReceivedFieldKey,
  ReceivedInvoiceMapping,
} from "@/lib/receivedInvoiceMapping";
import type { ReceivedInvoiceRow } from "@/lib/a3ecoExportReceived";
import { normalizeOperationType } from "@/lib/supplierMaster";
import { resolveGroupedTotal } from "@/lib/invoiceLineTotals";

// Reutilizamos el preview del módulo de emitidas (idéntico comportamiento).
export const previewReceivedExcel = previewExcelIssued;

// ── Helpers de conversión por celda (duplicados de issued) ────────────────

const readWorkbook = async (file: File): Promise<ExcelJS.Workbook> => {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb;
};

const cellToString = (raw: ExcelJS.CellValue): string => {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number") return String(raw);
  if (raw instanceof Date) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof raw === "object") {
    const obj = raw as { text?: string; result?: unknown; richText?: { text: string }[] };
    if (typeof obj.text === "string") return obj.text.trim();
    if (Array.isArray(obj.richText)) return obj.richText.map((r) => r.text).join("").trim();
    if (obj.result !== undefined) return String(obj.result).trim();
  }
  return String(raw);
};

type FormulaCellValue = ExcelJS.CellFormulaValue | (ExcelJS.CellSharedFormulaValue & { readonly formula: string });
const isFormulaCell = (raw: ExcelJS.CellValue): raw is FormulaCellValue =>
  !!raw && typeof raw === "object" && "formula" in raw && typeof (raw as FormulaCellValue).formula === "string";

const splitFormulaArgs = (args: string): string[] => {
  const out: string[] = [];
  let current = "";
  let depth = 0;
  let inQuotes = false;
  for (let i = 0; i < args.length; i++) {
    const ch = args[i];
    if (ch === '"') inQuotes = !inQuotes;
    if (!inQuotes && ch === "(") depth++;
    if (!inQuotes && ch === ")") depth--;
    if (!inQuotes && depth === 0 && (ch === "," || ch === ";")) {
      out.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  if (current.trim()) out.push(current.trim());
  return out;
};

const excelSerialToDateString = (n: number): string => {
  if (!Number.isFinite(n)) return String(n);
  const utcDays = Math.floor(n - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);
  const y = dateInfo.getUTCFullYear();
  const m = String(dateInfo.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dateInfo.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const textFormat = (value: string, format: string): string => {
  const digits = value.replace(/\D/g, "");
  if (/^0+$/.test(format) && digits) return digits.padStart(format.length, "0");
  return value;
};

const evaluateFormulaText = (
  worksheet: ExcelJS.Worksheet,
  rowIndex: number,
  formula: string,
  visited: Set<string>,
): string | null => {
  const f = formula.replace(/^=/, "").replace(/_xlfn\./gi, "").trim();
  const evalToken = (token: string): string => {
    const t = token.trim();
    const quoted = t.match(/^"(.*)"$/);
    if (quoted) return quoted[1];
    const cellRef = t.match(/^\$?([A-Z]{1,3})\$?(\d+)$/i);
    if (cellRef) {
      const colIdx = columnLetterToIndex(cellRef[1]);
      const refRow = parseInt(cellRef[2], 10);
      const address = `${cellRef[1].toUpperCase()}${refRow}`;
      if (visited.has(address)) return "";
      visited.add(address);
      const cell = worksheet.getRow(refRow).getCell(colIdx);
      const raw = cell.value;
      const nestedFormula =
        isFormulaCell(raw) && raw.formula
          ? raw.formula
          : (typeof (cell as unknown as { formula?: string }).formula === "string"
              ? (cell as unknown as { formula: string }).formula
              : null);
      if (nestedFormula) {
        const nested = evaluateFormulaText(worksheet, refRow, nestedFormula, visited);
        if (nested !== null) return nested;
      }
      if (raw instanceof Date) return cellToString(raw);
      if (typeof raw === "number" && cell.numFmt?.toLowerCase().includes("yy")) return excelSerialToDateString(raw);
      if (cell.text && cell.text !== "Invalid Date" && !/^\w{3}\s\w{3}\s\d{1,2}\s/i.test(cell.text)) return cell.text.trim();
      return cellToString(raw).trim();
    }
    return t;
  };
  const concat = f.match(/^(?:CONCAT|CONCATENATE)\((.*)\)$/i);
  if (concat) return splitFormulaArgs(concat[1]).map(evalToken).join("");
  const text = f.match(/^TEXT\((.*)\)$/i);
  if (text) {
    const [valueArg, formatArg] = splitFormulaArgs(text[1]);
    const format = (formatArg || "").replace(/^"|"$/g, "");
    return textFormat(evalToken(valueArg), format);
  }
  if (f.includes("&")) return splitFormulaArgs(f.replace(/&/g, ",")).map(evalToken).join("");
  if (/^\$?[A-Z]{1,3}\$?\d+$/i.test(f)) return evalToken(f);
  return null;
};

const parseDate = (raw: ExcelJS.CellValue, fmt: ReceivedDefaults["dateFormat"]): string => {
  if (raw instanceof Date) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = cellToString(raw);
  if (!s) return "";
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;
  const slash = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (slash) {
    let [, a, b, y] = slash;
    if (y.length === 2) y = (parseInt(y) >= 70 ? "19" : "20") + y;
    const useMdy = fmt === "mm/dd/yyyy";
    const dd = useMdy ? b : a;
    const mm = useMdy ? a : b;
    return `${y}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  return s;
};

const parseNumber = (raw: ExcelJS.CellValue, sep: ReceivedDefaults["decimalSeparator"]): number => {
  if (raw === null || raw === undefined || raw === "") return 0;
  if (typeof raw === "number") return raw;
  let s = cellToString(raw);
  if (!s) return 0;
  s = s.replace(/€/g, "").replace(/\u00a0/g, "").replace(/\s+/g, "").trim();
  if (sep === ",") {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (sep === ".") {
    s = s.replace(/,/g, "");
  } else {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
    else if (lastDot > lastComma) s = s.replace(/,/g, "");
  }
  return parseNumericLike(s);
};

// ── Getters de celda ──────────────────────────────────────────────────────

const getCellValue = (
  worksheet: ExcelJS.Worksheet,
  rowIndex: number,
  field: ReceivedFieldKey,
  columnMap: ReceivedColumnMap,
): ExcelJS.CellValue => {
  const letter = columnMap[field];
  if (!letter) return null;
  const colIdx = columnLetterToIndex(letter);
  if (colIdx <= 0) return null;
  return worksheet.getRow(rowIndex).getCell(colIdx).value;
};

// Extrae la fórmula de una celda contemplando fórmulas compartidas
// (ExcelJS guarda `sharedFormula` en lugar de `formula` en las celdas
// referenciadas, pero expone `cell.formula` como getter ya traducido).
const getCellFormula = (cell: ExcelJS.Cell): string | null => {
  const raw = cell.value;
  if (isFormulaCell(raw) && raw.formula) return raw.formula;
  const translated = (cell as unknown as { formula?: string }).formula;
  if (typeof translated === "string" && translated.trim()) return translated;
  return null;
};

const getCellText = (
  worksheet: ExcelJS.Worksheet,
  rowIndex: number,
  field: ReceivedFieldKey,
  columnMap: ReceivedColumnMap,
): string => {
  const letter = columnMap[field];
  if (!letter) return "";
  const colIdx = columnLetterToIndex(letter);
  if (colIdx <= 0) return "";
  const cell = worksheet.getRow(rowIndex).getCell(colIdx);
  const raw = cell.value;
  const formula = getCellFormula(cell);
  if (formula) {
    const evaluated = evaluateFormulaText(worksheet, rowIndex, formula, new Set([cell.address]));
    if (evaluated !== null && evaluated.trim()) return evaluated.trim();
  }
  const asText = (cell.text ?? "").toString().trim();
  // Si el formato de número aplica una máscara de fecha a un valor no-fecha
  // ExcelJS devuelve "Invalid Date"; ignorarlo y caer al valor bruto.
  if (asText && asText !== "Invalid Date") return asText;
  return cellToString(raw);
};

// ── Import principal ──────────────────────────────────────────────────────

export interface ReceivedImportResult {
  rows: ReceivedInvoiceRow[];
  notes: string[];
}

export const importReceivedExcel = async (
  file: File,
  mapping: ReceivedInvoiceMapping,
): Promise<ReceivedImportResult> => {
  const wb = await readWorkbook(file);
  const ws = wb.worksheets[Math.max(0, Math.min(mapping.sheet_index, wb.worksheets.length - 1))];
  if (!ws) return { rows: [], notes: ["No se pudo abrir la hoja seleccionada."] };

  const notes: string[] = [];
  const rows: ReceivedInvoiceRow[] = [];
  const defaults = mapping.defaults || {};
  const map = mapping.column_map || {};

  const startRow = mapping.header_row + 1;
  const endRow = ws.actualRowCount;

  for (let r = startRow; r <= endRow; r++) {
    const row = ws.getRow(r);
    if (!row.hasValues) continue;

    const invoiceNumberRaw = getCellText(ws, r, "invoiceNumber", map);
    if (!invoiceNumberRaw) continue;

    const invoiceDate = parseDate(getCellValue(ws, r, "invoiceDate", map), defaults.dateFormat || "auto");
    if (!invoiceDate) {
      notes.push(`Fila ${r}: factura ${invoiceNumberRaw} sin fecha válida — se omite.`);
      continue;
    }
    const accountingDateRaw = parseDate(getCellValue(ws, r, "accountingDate", map), defaults.dateFormat || "auto");
    const accountingDate = accountingDateRaw || invoiceDate;

    const series = getCellText(ws, r, "series", map);
    const supplierName = getCellText(ws, r, "supplierName", map);
    const supplierTaxId = getCellText(ws, r, "supplierTaxId", map)
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, "");

    const supplierAccountRaw = getCellText(ws, r, "supplierAccount", map);
    const supplierAccount = supplierAccountRaw || defaults.defaultSupplierAccount || "";
    if (!supplierAccountRaw && !defaults.defaultSupplierAccount) {
      notes.push(
        `Fila ${r}: factura ${invoiceNumberRaw} sin cuenta de proveedor (columna vacía y sin cuenta por defecto).`,
      );
    }
    const expenseAccountRaw = getCellText(ws, r, "expenseAccount", map);
    const expenseAccount = expenseAccountRaw || defaults.defaultExpenseAccount || "";

    const sep = defaults.decimalSeparator || "auto";
    const taxableBase = parseNumber(getCellValue(ws, r, "taxableBase", map), sep);
    const vatAmount = parseNumber(getCellValue(ws, r, "vatAmount", map), sep);
    const totalAmount = parseNumber(getCellValue(ws, r, "totalAmount", map), sep);
    const irpfAmount = parseNumber(getCellValue(ws, r, "irpfAmount", map), sep);
    const surchargeAmount = parseNumber(getCellValue(ws, r, "surchargeAmount", map), sep);

    let vatRate = parseNumber(getCellValue(ws, r, "vatRate", map), sep);
    if (!vatRate && taxableBase) {
      const computed = (vatAmount / taxableBase) * 100;
      const candidates = [0, 4, 10, 21];
      vatRate = candidates.reduce((best, c) =>
        Math.abs(c - computed) < Math.abs(best - computed) ? c : best, 0);
    }
    let irpfRate = parseNumber(getCellValue(ws, r, "irpfRate", map), sep);
    if (!irpfRate && irpfAmount && taxableBase) {
      irpfRate = Math.round((Math.abs(irpfAmount) / Math.abs(taxableBase)) * 10000) / 100;
    }
    let surchargeRate = parseNumber(getCellValue(ws, r, "surchargeRate", map), sep);
    if (!surchargeRate && surchargeAmount && taxableBase) {
      surchargeRate = Math.round((Math.abs(surchargeAmount) / Math.abs(taxableBase)) * 10000) / 100;
    }

    const postalCode = getCellText(ws, r, "postalCode", map);
    const opTypeText = getCellText(ws, r, "operationType", map);
    const operationType = opTypeText
      ? normalizeOperationType(opTypeText)
      : defaults.defaultOperationType || "interior";
    const vatLedgerNumber = getCellText(ws, r, "vatLedgerNumber", map);
    const paymentMethod = getCellText(ws, r, "paymentMethod", map);

    rows.push({
      date: accountingDate,
      invoiceDate,
      series: series || undefined,
      invoiceNumber: invoiceNumberRaw,
      vatLedgerNumber: vatLedgerNumber || undefined,
      supplierName,
      supplierTaxId,
      supplierAccount,
      expenseAccount,
      vatLines: [{ taxableBase, vatRate, vatAmount, surchargeRate, surchargeAmount }],
      irpfRate: irpfRate || null,
      irpfAmount: irpfAmount || null,
      totalAmount,
      postalCode: postalCode || undefined,
      operationType,
      paymentMethod: paymentMethod || undefined,
    });
  }

  // Agrupar filas con misma serie+nº+fecha+NIF (una fila por tipo IVA en el ERP).
  // Si viene nº registro IVA, éste manda: filas con el mismo nº se agrupan aunque cambie el resto.
  const grouped = new Map<string, { row: ReceivedInvoiceRow; totals: number[] }>();
  for (const row of rows) {
    const key = row.vatLedgerNumber
      ? `LEDGER|${row.vatLedgerNumber}`
      : `${row.series ?? ""}|${row.invoiceNumber}|${row.invoiceDate}|${row.supplierTaxId}`;
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, { row: { ...row, vatLines: [...row.vatLines] }, totals: [row.totalAmount] });
    } else {
      existing.row.vatLines.push(...row.vatLines);
      existing.totals.push(row.totalAmount);
      if (!existing.row.irpfAmount && row.irpfAmount) existing.row.irpfAmount = row.irpfAmount;
      if (!existing.row.irpfRate && row.irpfRate) existing.row.irpfRate = row.irpfRate;
    }
  }

  // El total puede venir repetido (global) o por línea según el ERP:
  // resolveGroupedTotal detecta la convención y acumula sólo si procede.
  const finalRows = Array.from(grouped.values()).map((g) =>
    resolveGroupedTotal(g.row, g.totals),
  );

  return { rows: finalRows, notes };
};
